// Copyright ©️ 2026 eVolve MEP, LLC

import React, { type ReactNode, useMemo } from 'react';

import { AppShell } from '@mantine/core';
import { useDisclosure, useViewportSize } from '@mantine/hooks';

import { useGeneralContext } from 'helpers/useGeneralContext';
import { useUserPreferences } from 'hooks/typeCacheHooks/useUserPreferences/useUserPreferences';
import { useIsMobile } from 'hooks/useIsMobile';

type AppShellContextType = {
  navbarExpanded: boolean;
  setNavbarExpanded: (opened: boolean) => void;
  navbarWidth: number;
  /** Whether the mobile navbar (a right `AppShell.Aside` overlay) is open. Only relevant on mobile. */
  mobileNavOpened: boolean;
  toggleMobileNav: () => void;
  closeMobileNav: () => void;
};

const NAV_BAR_EXPANDED_WIDTH = 180;
const NAV_BAR_COLLAPSED_WIDTH = 48;
/** Fallback mobile navbar width before the viewport size is measured (the overlay is full-screen on mobile). */
const MOBILE_NAV_WIDTH = 280;
export const NAV_BAR_FOOTER_HEIGHT = 50;
export const MOBILE_HEADER_HEIGHT = 56;

/** in ms */
export const navbarAnimationDuration = 300;
export const navbarAnimationTimingFunction = 'ease-in';
export const navbarTransitionStyle = `all ${navbarAnimationDuration}ms ${navbarAnimationTimingFunction}`;

const AppShellContext = React.createContext<AppShellContextType | undefined>(undefined);

export const AppShellProvider = ({ children }: { children: ReactNode }) => {
  // Used on desktop to remember whether the navbar is expanded or collapsed.
  const [navbarExpandedPreference, setNavbarExpanded] = useUserPreferences('NAV_BAR_EXPANDED', true);
  const isMobile = useIsMobile();
  const { width: viewportWidth } = useViewportSize();
  const [mobileNavOpened, { toggle: toggleMobileNav, close: closeMobileNav }] = useDisclosure(false);

  // On mobile the navbar never collapses to icons — it fills the screen, shown/hidden as a full-width overlay.
  const navbarExpanded = isMobile || navbarExpandedPreference;
  const navbarWidth = useMemo(
    () =>
      isMobile ? viewportWidth || MOBILE_NAV_WIDTH : navbarExpanded ? NAV_BAR_EXPANDED_WIDTH : NAV_BAR_COLLAPSED_WIDTH,
    [isMobile, navbarExpanded, viewportWidth],
  );
  const value = useMemo<AppShellContextType>(
    () => ({
      navbarExpanded,
      setNavbarExpanded,
      navbarWidth,
      mobileNavOpened,
      toggleMobileNav,
      closeMobileNav,
    }),
    [navbarExpanded, navbarWidth, setNavbarExpanded, mobileNavOpened, toggleMobileNav, closeMobileNav],
  );
  return (
    <AppShellContext value={value}>
      <AppShell
        bg={isMobile ? 'white' : 'app-shell-gray.9'}
        header={isMobile ? { height: MOBILE_HEADER_HEIGHT } : undefined}
        navbar={isMobile ? undefined : { width: navbarWidth, breakpoint: 'xs' }}
        // On mobile the nav lives in a right-side Aside overlay (matching the header burger's side).
        aside={isMobile ? { width: navbarWidth, breakpoint: 'sm', collapsed: { mobile: !mobileNavOpened } } : undefined}
        transitionDuration={navbarAnimationDuration}
        transitionTimingFunction={navbarAnimationTimingFunction}
      >
        {children}
      </AppShell>
    </AppShellContext>
  );
};

export const useAppShell = () => useGeneralContext(AppShellContext, 'AppShell');
